// SPDX-License-Identifier: MIT
// ErrorClassifier - maps raw errors to structured categories (Phase 5)

import { ClassifiedError, ErrorCategory, IntegrityIssue } from '../download/types';

// Lightweight Azure-like status code patterns (avoid SDK deep coupling for testability)
const TRANSIENT_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
const AUTH_STATUS = new Set([401, 403]);
const NOT_FOUND_STATUS = new Set([404]);
const PERMISSION_CODES = ['AuthorizationFailure', 'AuthenticationFailed'];
const THROTTLE_CODES = ['TooManyRequests', 'ServerBusy'];

export interface ErrorClassifierOptions {
  integrityIssueAs?: ErrorCategory; // default 'integrity'
}

export class ErrorClassifier {
  private integrityIssueAs: ErrorCategory;
  constructor(opts: ErrorClassifierOptions = {}) {
    this.integrityIssueAs = opts.integrityIssueAs ?? 'integrity';
  }

  classify(e: unknown): ClassifiedError {
    // Integrity issues propagated as arrays
    if (Array.isArray(e) && e.length && (e[0] as IntegrityIssue).kind) {
      return {
        category: this.integrityIssueAs,
        message: this.joinIssues(e as IntegrityIssue[]),
        retryable: false,
      };
    }
    if (typeof e === 'string') {
      return this.classifyMessage(e);
    }
    interface BasicErr {
      message?: string;
      statusCode?: number;
      status?: number;
      code?: string | number;
      [k: string]: unknown;
    }
    const err = typeof e === 'object' && e ? (e as BasicErr) : ({} as BasicErr);
    const message: string = err.message || 'Unknown error';
    const statusCode: number | undefined =
      typeof err.statusCode === 'number'
        ? err.statusCode
        : typeof err.status === 'number'
          ? err.status
          : undefined;
    const code: string | number | undefined = err.code;

    // Aborted / cancelled
    if (code === 'ABORT_ERR' || message.includes('abort') || message.includes('cancelled')) {
      return { category: 'cancelled', message: 'Operation cancelled', retryable: false, code };
    }

    // Timeout heuristics
    if (message.toLowerCase().includes('timeout')) {
      return { category: 'timeout', message, retryable: true, code, statusCode };
    }

    if (statusCode) {
      if (AUTH_STATUS.has(statusCode))
        return { category: 'auth', message, retryable: false, statusCode, code };
      if (NOT_FOUND_STATUS.has(statusCode))
        return { category: 'not-found', message, retryable: false, statusCode, code };
      if (TRANSIENT_STATUS.has(statusCode))
        return { category: 'network', message, retryable: true, statusCode, code };
    }

    if (code) {
      if (PERMISSION_CODES.includes(code as string))
        return { category: 'permission', message, retryable: false, code };
      if (THROTTLE_CODES.includes(code as string))
        return { category: 'throttle', message, retryable: true, code };
    }

    // Content / integrity heuristics
    if (/integrity|hash|corrupt|mismatch/i.test(message)) {
      return { category: 'integrity', message, retryable: false, code, statusCode };
    }

    // Network generic
    if (/network|connection|fetch failed|ECONN|ENET/i.test(message)) {
      return { category: 'network', message, retryable: true, code, statusCode };
    }

    return { category: 'unknown', message, retryable: false, code, statusCode };
  }

  private classifyMessage(msg: string): ClassifiedError {
    const lower = msg.toLowerCase();
    if (lower.includes('unauth') || lower.includes('forbidden'))
      return { category: 'auth', message: msg, retryable: false };
    if (lower.includes('not found'))
      return { category: 'not-found', message: msg, retryable: false };
    if (lower.includes('timeout')) return { category: 'timeout', message: msg, retryable: true };
    if (lower.includes('throttle') || lower.includes('rate limit'))
      return { category: 'throttle', message: msg, retryable: true };
    if (lower.includes('integrity') || lower.includes('hash') || lower.includes('corrupt'))
      return { category: 'integrity', message: msg, retryable: false };
    if (lower.includes('network') || lower.includes('connection'))
      return { category: 'network', message: msg, retryable: true };
    return { category: 'unknown', message: msg, retryable: false };
  }

  private joinIssues(issues: IntegrityIssue[]): string {
    return issues.map(i => i.message).join('; ');
  }
}
